import { Box, Portal } from '@chakra-ui/react';
import { AnimatePresence } from 'framer-motion';
import { get } from 'lodash';
import { DateTime } from 'luxon';
import React, { lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { FadeSlideMotionBox } from '../../components/Motion/FadeSlideMotionBox';
import { Flex } from '../../components/UI-CHAKRA/Flex';
import withSuspense from '../../components/withLazyComponent';
import { HEADER_HEIGHT, HEADER_HEIGHT_MOBILE } from '../../contants/size';
import { useIsMobile } from '../../helpers/useIsMobile';
import { Tracker } from '../../shared/tracker/Tracker';
import { useDrawerStore } from '../../stores/drawerStore';
import { useInteractionStore } from '../../stores/interactionStore';
import { defaultWidthTransition, useMyCentralStore } from '../../stores/myCentralStore';
import { usePrescriptionLayoutStore } from '../../stores/prescriptionLayoutStore';
import { usePrescriptionStore } from '../../stores/prescriptionStore';
import News from '../my-central/Prescriber/News';
import SideDrawer from '../my-central/SideDrawer';
import { MENUS } from '../my-central/constants';
import { amplitudeModule } from '../../shared/amplitude';
import ConsentDialog from './ConsentDialog/ConsentDialog';
import MyCentral from './MyCentral';
import PrescriptionFooter from './PrescriptionFooter';
import PrescriptionHeader from './PrescriptionHeader/PrescriptionHeader';
import { UserSignUpResponse } from './service';
import { TrackerEventService } from './trackerEventService';
import { trackingService } from '../../shared/eventTracking';

const ContainerWithRef = styled(Box)`
  &::-webkit-scrollbar {
    width: 9px;
  }
`;

interface PrescriptionLayoutProps {
  continueButtonLabel: string;
  cancelButtonLabel?: string;
  children: React.ReactNode;
  onCancel?(): void;
  onContinue(): void;
  loading: boolean;
  showMyCentral: boolean;
  continueDisabled?: boolean;
  showMenu?: boolean;
  hideHeader?: boolean;
  userSignUpStatus?: UserSignUpResponse | null;
}

const PatientData = withSuspense(lazy(() => import('../my-central/PatientData')));
const DrawerModels = withSuspense(lazy(() => import('../my-central/Models/DrawerModels')));
const DrugInteractions = withSuspense(lazy(() => import('../my-central/DrugInteractions')));
const Policy = withSuspense(lazy(() => import('../my-central/Policy')));
const Help = withSuspense(lazy(() => import('../my-central/Help')));
const History = withSuspense(lazy(() => import('../my-central/History/History')));
const PrescriberCentral = withSuspense(lazy(() => import('../my-central/Prescriber/PrescriberCentral')));
const Office = withSuspense(lazy(() => import('../my-central/Office')));

const MENU_LABELS: Record<MENUS, string> = {
  [MENUS.PATIENT_DATA]: 'Perfil do paciente',
  [MENUS.MODELS]: 'Modelos',
  [MENUS.DRUG_INTERACTIONS]: 'Interações',
  [MENUS.PERSONAL_SETTINGS]: 'Configurações',
  [MENUS.POLICY]: 'Política',
  [MENUS.HELP]: 'Ajuda',
  [MENUS.HISTORY]: 'Histórico',
  [MENUS.PRESCRIBER_DATA]: 'Dados do prescritor',
  [MENUS.News]: 'Novidades',
  [MENUS.OFFICE]: 'Mevo App',
};

function PrescriptionLayout(props: PrescriptionLayoutProps) {
  const rootContainerRef = useRef(null);

  const { prescription } = usePrescriptionStore();

  const { medicineId, numberOfInteractions, interactionId, medicineDrugInteractions } = useInteractionStore();

  const { setDrawerOpen, drawerOpen, selectedMenu, setSelectedMenu } = useDrawerStore();
  const { prescriberConfiguration } = usePrescriptionStore();

  const { setScrollTop } = usePrescriptionLayoutStore();

  const sideBarWidth = useMyCentralStore((state) => state.sideBarWidth);

  const medicines = usePrescriptionStore((state) => state.prescription?.Itens);
  const viewByActivePrinciple = usePrescriptionStore((state) => state.prescription?.ExibirPorPrincipioAtivo);

  const [newsAreOpen, setNewsAreOpen] = useState(false);
  const [canShowConsentDialog, setCanShowConsentDialog] = useState(false);

  const enableBanner = prescriberConfiguration?.Features?.enablePharmacyAds;

  const isMobile = useIsMobile();
  const match = useRouteMatch();

  useEffect(() => {
    if (prescription && prescription.idPrescricao && prescription.Prescritor && prescription.Prescritor.idPrescritor) {
      setCanShowConsentDialog(true);
    } else {
      setCanShowConsentDialog(false);
    }
  }, [prescription]);

  const switchComponent = () => {
    switch (selectedMenu) {
      case MENUS.PATIENT_DATA:
        return <PatientData />;
      case MENUS.MODELS:
        return <DrawerModels />;
      case MENUS.DRUG_INTERACTIONS:
        return (
          <DrugInteractions
            medicinesInteractions={medicineDrugInteractions}
            medicines={medicines || []}
            interactionId={interactionId}
            medicineId={medicineId}
            numberOfInteractions={numberOfInteractions}
            onClickPolicy={onClickPolicy}
            viewByActivePrinciple={viewByActivePrinciple || false}
          />
        );
      case MENUS.POLICY:
        return <Policy onClickGoBack={onClickGoBackFromPolicy} numberOfInteractions={numberOfInteractions} />;
      case MENUS.HELP:
        return <Help />;
      case MENUS.HISTORY:
        return <History />;
      case MENUS.PRESCRIBER_DATA:
        return <PrescriberCentral />;
      case MENUS.OFFICE:
        return (
          <Office
            onClickExit={() => {
              trackingService.trackOfficeCloseSidebar();
              setDrawerOpen(false);
              setSelectedMenu('');
            }}
            resendCountdown={30}
            qrCodeLink={props.userSignUpStatus?.data?.url || ''}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (props.userSignUpStatus?.enabled) {
      setSelectedMenu(MENUS.OFFICE);
      setDrawerOpen(true);
    }
  }, [props.userSignUpStatus, setDrawerOpen, setSelectedMenu])

  const onClickPolicy = () => {
    setSelectedMenu(MENUS.POLICY);
  };

  const onClickGoBackFromPolicy = () => {
    setSelectedMenu(MENUS.DRUG_INTERACTIONS);
  };

  const onSelectMenu = (value: string | boolean) => {
    if (value === selectedMenu) {
      setDrawerOpen(false);
      setSelectedMenu('');
      return;
    }

    if (typeof value === 'boolean') {
      if (value && !selectedMenu) {
        setSelectedMenu(MENUS.PATIENT_DATA);
      }

      setDrawerOpen(value);
      return;
    }

    // @ts-ignore
    setSelectedMenu(value);
    // @ts-ignore
    const open = !drawerOpen || (drawerOpen && value !== selectedMenu);
    setDrawerOpen(open);

    const menuLabel = MENU_LABELS[value as MENUS] ?? String(value);
    amplitudeModule.menuClicked({
      screen_name: 'prescrição',
      name: menuLabel,
      item: [{ id: 0, name: menuLabel }],
      partner_id: prescriberConfiguration?.idParceiro ?? 0,
      partner_name: prescriberConfiguration?.Nome ?? '',
      establishment_name: prescription?.Estabelecimento?.Nome ?? '',
    });

    let event = '';
    switch (value) {
      case MENUS.PATIENT_DATA: {
        event = 'Open Patient Info Central';
        break;
      }
      case MENUS.HELP: {
        event = 'Help Central';
        break;
      }
      case MENUS.DRUG_INTERACTIONS: {
        event = 'Open Interactions Central';
        break;
      }
      case MENUS.MODELS: {
        event = 'Open Model Central';
        break;
      }
      case MENUS.HISTORY: {
        event = 'Open Prescription History';
        break;
      }
      case MENUS.News: {
        trackEventClickedNews();
        setNewsAreOpen(true);
        break;
      }

      case MENUS.OFFICE: {
        trackingService.trackOfficeMenuSidebarClick();
        break;
      }
    }

    if (event) {
      Tracker.trackEvent({ event });
    }
  };

  const trackEventClickedNews = async () => {
    if (prescription)
      await TrackerEventService.track(prescription.idPrescricao, 'backoffice_prescription_icons_clicou', {
        idPrescricao: prescription?.idPrescricao,
        idMedico: prescription?.idPrescritor,
        codigoValidacao: prescription?.CodigoValidacao,
        idParceiro: prescriberConfiguration?.idParceiro,
        categoria: 'novidades',
        timeStamp: DateTime.now(),
      });
  };

  const onContinue = useCallback(() => {
    props.onContinue();
    setDrawerOpen(false);
  }, [setDrawerOpen, props]);

  const containerHeight = useMemo(() => {
    if (isMobile && drawerOpen) {
      return `calc(100vh - ${HEADER_HEIGHT_MOBILE})`;
    }

    if (isMobile && !drawerOpen) {
      return `calc(100vh - ${HEADER_HEIGHT_MOBILE})`;
    }

    if (props.hideHeader) {
      return `calc(100vh)`;
    }

    return `calc(100vh - ${HEADER_HEIGHT})`;
  }, [isMobile, drawerOpen, props.hideHeader]);

  const containerWidth = useMemo(() => {
    if (!props.showMyCentral) return '100%';
    if (
      match.path === '/v2/prescription/:prescriptionReference/:basicAuth' ||
      match.path === '/v3/prescription/:prescriptionReference'
    ) {
      return `calc(100% - ${sideBarWidth})`;
    }
    return '100%';
  }, [props.showMyCentral, match.path, sideBarWidth]);

  const canUsePrescriptionHistory =
    get(prescriberConfiguration, 'Features.useCookieAuth', false) ||
    get(prescriberConfiguration, 'Features.canUseHistoricWithBasic', false);

  const handleScrollTop = useCallback(
    (e: Event) => {
      const targetDiv: Element = e.target as Element;

      setScrollTop(targetDiv.scrollTop);
    },
    [setScrollTop]
  );

  useEffect(() => {
    const container = document.getElementById('Container');

    if (!container) return;

    container.addEventListener('scroll', handleScrollTop);
    return () => {
      container.removeEventListener('scroll', handleScrollTop);
    };
  }, [handleScrollTop]);

  useEffect(() => {
    if (!isMobile) return;

    const handleFocus = (e: any) => {
      const focusedElement = e.target;

      if (focusedElement && (focusedElement.tagName === 'INPUT' || focusedElement.tagName === 'TEXTAREA')) {
        setTimeout(() => {
          focusedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
      }
    };
    document.addEventListener('focus', handleFocus, true);

    return () => {
      document.removeEventListener('focus', handleFocus, true);
    };
  }, [isMobile]);

  return (
    <Flex direction="column" h="100vh" maxH="100vh" overflow="hidden">
      {!props.hideHeader && (
        <Box style={{ padding: 0 }}>
          <PrescriptionHeader showMenu={props.showMenu} />
        </Box>
      )}

      <Flex flex="1" minH={0} id="PrescriptionContent" backgroundColor={'#f8f9fa'} position={'relative'} padding={0}>
        <Flex
          direction="column"
          flex="1"
          width={containerWidth}
          transition={defaultWidthTransition}
          position="relative"
        >
          <ContainerWithRef
            id="Container"
            position={'relative'}
            flex="1"
            overflowY="auto"
            height={containerHeight}
            width={containerWidth}
            transition={defaultWidthTransition}
            ref={rootContainerRef as any}
            paddingBottom={{ base: enableBanner ? '60px' : '40px', md: '0px' }}
          >
            {props.children}
          </ContainerWithRef>
          <PrescriptionFooter
            onCancel={props.onCancel}
            onContinue={onContinue}
            continueLabel={props.continueButtonLabel}
            loading={props.loading}
            continueDisabled={props.continueDisabled}
            cancelLabel={props.cancelButtonLabel || 'Cancelar'}
          />
        </Flex>

        <AnimatePresence>
          {props.showMyCentral && (
            <FadeSlideMotionBox key={'my-central'}>
              {selectedMenu !== MENUS.News && <SideDrawer open={drawerOpen}>{switchComponent()}</SideDrawer>}

              <MyCentral
                onSelectMenu={onSelectMenu}
                open={drawerOpen}
                canUsePrescriptionHistory={canUsePrescriptionHistory}
                selectedMenu={selectedMenu}
                userSignUpStatus={props.userSignUpStatus}
              />

              {drawerOpen && (
                <Box
                  top={0}
                  right={0}
                  bottom={0}
                  left={0}
                  position={'absolute'}
                  zIndex={1}
                  onClick={() => setDrawerOpen(false)}
                />
              )}
            </FadeSlideMotionBox>
          )}
        </AnimatePresence>
      </Flex>
      {canShowConsentDialog && prescription && prescription.Prescritor && (
        <Portal>
          <ConsentDialog
            doctorId={prescription.Prescritor.idPrescritor}
            prescriberEmail={prescription.Prescritor.Email}
            prescriptionId={prescription.idPrescricao}
          />
        </Portal>
      )}
      {selectedMenu === MENUS.News && newsAreOpen && (
        <Portal>
          <News
            onClose={() => {
              setNewsAreOpen(false);
              setDrawerOpen(false);
            }}
          />
        </Portal>
      )}
    </Flex>
  );
}

export default React.memo(PrescriptionLayout);
